import { supabase } from '../client';
import type { Database } from '../generated';

export type Organization = Database['core']['Tables']['organizations']['Row'];

export async function listMyOrganizations(): Promise<Organization[]> {
  // RLS limits this to orgs the current user has membership in. A user
  // belonging to hundreds of orgs is unrealistic but we cap defensively.
  const { data, error } = await supabase()
    .schema('core')
    .from('organizations')
    .select('*')
    .is('archived_at', null)
    .order('name', { ascending: true })
    .limit(500);
  if (error) throw error;
  return data ?? [];
}

/**
 * Window (in days) during which an archived org can still be restored by
 * its own owner/admin via {@link unarchiveMyOrganization}. Mirrors the
 * `perma_lock_at = archived_at + 30 days` cascade stamped onto stores by
 * `core.rpc_admin_archive_organization`. After this window the org
 * perma-locks and only PeptideClients support can recover it.
 */
export const ORG_ARCHIVE_RESTORE_WINDOW_DAYS = 30;

/**
 * The archived orgs the current user still belongs to (RLS-scoped). These
 * are intentionally excluded from {@link listMyOrganizations}; the
 * workspace picker surfaces them in a collapsed "Archived organizations"
 * section so active workspaces stay uncluttered.
 */
export async function listMyArchivedOrganizations(): Promise<Organization[]> {
  const { data, error } = await supabase()
    .schema('core')
    .from('organizations')
    .select('*')
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return data ?? [];
}

/**
 * The UTC instant after which an archived org can no longer be restored by
 * its owner/admin (the server enforces the same boundary). Returns null
 * when the org is not archived or carries an unparseable timestamp.
 */
export function orgRestoreDeadline(archivedAt: string | null | undefined): Date | null {
  if (!archivedAt) return null;
  const t = new Date(archivedAt).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(t + ORG_ARCHIVE_RESTORE_WINDOW_DAYS * 24 * 60 * 60 * 1000);
}

/**
 * Self-service restore of an archived org by its owner/admin, allowed only
 * inside the {@link ORG_ARCHIVE_RESTORE_WINDOW_DAYS} window. Reactivates
 * the org plus its cascade-archived stores. Throws the server's friendly
 * message when the caller lacks the role or the window has closed. The
 * RPC ships in `20260703090000_core_rpc_unarchive_my_organization.sql` and
 * isn't in the generated types yet, so we go through the `schema('core')`
 * cast pattern used elsewhere in this package.
 */
export async function unarchiveMyOrganization(orgId: string): Promise<Organization> {
  const core = supabase().schema('core') as unknown as {
    rpc: (
      name: string,
      args?: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: unknown }>;
  };
  const { data, error } = await core.rpc('rpc_unarchive_my_organization', {
    p_org_id: orgId,
  });
  if (error) throw error;
  const payload = data as { organization: Organization } | null;
  if (!payload?.organization) {
    throw new Error('rpc_unarchive_my_organization returned no organization.');
  }
  return payload.organization;
}

export interface BootstrapOrgInput {
  orgName: string;
  orgSlug: string;
  storeName?: string;
  storeSlug?: string;
  /**
   * Optional partner referral code. When present, `core.rpc_bootstrap_org`
   * attempts to lock a `partners.referrals` row atomically with org
   * creation. Failures here do NOT roll back org creation -- the partner
   * pairing can still be redeemed from the `/pair` route within 72h.
   */
  referralCode?: string | null;
}

export interface BootstrapOrgResult {
  orgId: string;
  storeId: string;
  /** True if the partner referral was successfully locked at signup. */
  referralLocked: boolean;
  /**
   * Non-null when a referral code was attempted but the lock failed
   * (e.g. 'expired', 'already_used', 'self_referral_blocked'). The
   * operator can re-attempt via /pair or contact support.
   */
  referralError: string | null;
}

/**
 * First-run onboarding: creates an organization, a default store, and an
 * owner membership for the current user in one transactional RPC. When
 * `referralCode` is provided, the matching `partners.referrals` row is
 * locked atomically (see `app_public.rpc_redeem_partner_code`).
 */
export async function bootstrapOrg(input: BootstrapOrgInput): Promise<BootstrapOrgResult> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_bootstrap_org', {
      p_org_name: input.orgName,
      p_org_slug: input.orgSlug,
      p_store_name: input.storeName ?? 'Main store',
      p_store_slug: input.storeSlug ?? 'main',
      p_referral_code: input.referralCode ?? null,
    });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('rpc_bootstrap_org returned no row.');
  return {
    orgId: row.org_id,
    storeId: row.store_id,
    referralLocked: Boolean(row.referral_locked),
    referralError: row.referral_error ?? null,
  };
}
